import { CATALOGO, type DashboardImgr, ITENS_POR_SETOR, SETORES } from "@caint/data";
import type { ChaveSelo, ItemAvaliado, Momento, NivelMaturidade, ResultadoImgq } from "@caint/engine";
import type {
  AbrirCicloDTO,
  AtualizarUsuarioDTO,
  CicloPublico,
  CriarUsuarioDTO,
  EditarCicloDTO,
  EscopoData,
  ItemCatalogo,
  MatrizUnidade,
  MatrizUploadDTO,
  PapelKey,
  RespostaItem,
  ResumoMatrizUnidade,
  SupervisaoData,
  UsuarioPublico,
} from "@caint/shared";
import {
  SETORES_OBRIGATORIOS,
  comObrigatorios,
  migrarSupervisao,
  motivoRecusaEvidencia,
  paraPushDados,
  resolverEscopo,
  supervisaoVazia,
  tipoDeEvidencia,
} from "@caint/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ApiClient,
  ApiError,
  type AvaliacaoRemota,
  type DecisaoMigracaoValor,
  type EvidenciaRemota,
  type ImpactoRemoto,
  type LoteDetalheRemoto,
  type LoteRemoto,
  type ManifestoRemoto,
  type PreflightRemoto,
  type ResultadoExecucaoRemoto,
  apiClient,
  paraUsuario,
} from "../lib/api.ts";
import { escolherAvaliacao } from "../lib/ciclos.ts";
import { prepararEvidencia } from "../lib/comprimirImagem.ts";
import { rotuloMes } from "../lib/datas.ts";
import { drenarEvidencia, drenarEvidencias, drenarExclusoes } from "../lib/drenarEvidencias.ts";
import { engine } from "../lib/engine.ts";
import { mensagemAmigavel } from "../lib/erros.ts";
import { papelEhGestao, papelSoLeitura } from "../lib/navegacao.ts";
import { type Panorama, panorama } from "../lib/panorama.ts";
import {
  type ItemAvaliavel,
  checklistLiberado,
  gerarAmostraCega,
  itemCompleto,
  notaPreenchida,
  overridesSupervisor,
  resetarAmostra,
  seedAmostra,
  tamanhoAmostraEfetivo,
} from "../lib/supervisao.ts";
import { type EntradaPendente, SupervisaoPendente, blobVigente, suja } from "../lib/supervisaoPendente.ts";
import { SyncOutbox } from "../lib/syncOutbox.ts";
import {
  DexieEvidenciaQueue,
  type EvidenciaPendente,
  type EvidenciaQueue,
} from "../persistence/evidenciaQueue.ts";
import { LocalStorageStore } from "../persistence/localStorageStore.ts";
import type { KeyValueStore } from "../persistence/port.ts";

const CHAVE = "caint-mobile-v2";

/** Acesso total à Supervisão AUDIN: o Supervisor (audin) + gestão (admin,
 * coord, presidente). Os demais papéis só leem. Espelha o gate do servidor. */
const PAPEIS_SUPERVISAO: ReadonlySet<PapelKey> = new Set<PapelKey>(["audin", "admin", "coord", "presidente"]);

const STATUS_INDISPONIVEL: ReadonlySet<number> = new Set([404, 502, 503, 504]);

/**
 * Traduz a falha do login para o que a pessoa precisa ler.
 *
 * O 403 carrega o motivo REAL vindo do servidor ("conta desativada") e é
 * repassado: dizer "falha no servidor" mandava o gestor caçar um problema que
 * não existia. O 401 continua genérico de propósito — ali a origem pode ser
 * e-mail ou senha, e distinguir permitiria enumerar contas.
 */
export function mensagemDeLogin(e: unknown): string {
  if (!(e instanceof ApiError)) {
    // Sem rede: o 1º acesso exige conexão; depois a sessão fica salva (offline).
    return "Sem conexão. É preciso estar online para o primeiro acesso.";
  }
  if (e.status === 401) {
    return "E-mail ou senha inválidos.";
  }
  if (e.status === 403 && e.message) {
    return e.message;
  }
  // 404 no login = a rota existe, então quem respondeu foi o Traefik sem a API
  // por trás (contêiner parado); 502/503/504 = API fora do ar ou reiniciando.
  if (STATUS_INDISPONIVEL.has(e.status)) {
    return "Servidor indisponível no momento. Tente novamente em alguns minutos.";
  }
  return "Falha no servidor. Tente novamente.";
}

/**
 * Monta e envia UM push da supervisão. Existe para o payload ser montado num
 * lugar só — estava duplicado no envio manual, no autosave e no `sync`, que é
 * exatamente como `resolucoes` some de um deles e ninguém percebe.
 */
async function pushSupervisao(
  api: ApiClient,
  token: string,
  avaliacaoId: string,
  dados: SupervisaoData,
  versaoBase: number | null,
  validar: boolean,
): Promise<{ aplicado: boolean; versao: number }> {
  const r = await api.pushSupervisao(
    {
      avaliacaoId,
      dados: paraPushDados(dados),
      validar,
      ...(versaoBase != null ? { versaoBase } : {}),
      atualizadoEm: new Date().toISOString(),
    },
    token,
  );
  return { aplicado: r.aplicado, versao: r.versao };
}

/**
 * Guarda das escritas com escopo de ciclo. Devolve o id do ciclo ativo ou `null`
 * — mas NUNCA em silêncio.
 *
 * Antes, cada escrita (`patch`, `editarSupervisao`, `editarEscopo`, …) fazia um
 * early-return mudo sem ciclo ativo. Como `podeEditar`/`editavel` não dependiam
 * do ciclo, a tela seguia parecendo editável e o preenchimento inteiro era
 * descartado sem um único aviso — foi assim que uma Supervisão AUDIN completa se
 * perdeu (17/ago/2026). O shell agora bloqueia essas telas (`exigeCicloSelecionado`),
 * então chegar aqui sem ciclo é bug de navegação: estoura em dev para o teste
 * pegar e registra em produção, sem derrubar o app em campo.
 */
function exigirCiclo(avaliacaoId: string | undefined, acao: string): string | null {
  if (avaliacaoId) {
    return avaliacaoId;
  }
  const msg = `[CAINT] "${acao}" ignorada: nenhum ciclo selecionado (a tela não deveria estar acessível).`;
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    throw new Error(msg);
  }
  console.error(msg);
  return null;
}

/** Fila de evidências (Dexie) como singleton lazy — criada só no navegador. */
let filaSingleton: EvidenciaQueue | null = null;
function filaPadrao(): EvidenciaQueue {
  filaSingleton ??= new DexieEvidenciaQueue();
  return filaSingleton;
}

/**
 * Store (localStorage) como singleton lazy — ESTÁVEL entre renders. Sem isto, o
 * default `new LocalStorageStore()` criava um store novo a cada render, tornando
 * `outbox`/`sync` instáveis e disparando os efeitos de sync em loop (storm de
 * `/sync/pull` → ERR_INSUFFICIENT_RESOURCES). Testes injetam o próprio store.
 */
let storeSingleton: KeyValueStore | null = null;
function storePadrao(): KeyValueStore {
  storeSingleton ??= new LocalStorageStore();
  return storeSingleton;
}

export interface UsuarioAutenticado {
  readonly id: string;
  readonly nome: string;
  readonly papel: PapelKey;
  readonly funcao: string;
}

/**
 * Sessão de uma conta `unidade`. Guardada SEPARADA da sessão interna (`auth`/
 * `token`) de propósito: com `token` interno nulo, todos os efeitos internos
 * (pull/sync/drenar, que dariam 401/403 para a unidade) viram no-op. Login é o
 * mesmo (a tela principal) — o app mostra só a view do Plano de Ação.
 */
export interface SessaoUnidade {
  readonly token: string;
  readonly nome: string;
  readonly cargo: string | null;
}

export type Aba = "inicio" | "checklist" | "risco" | "relatorio" | "mais";
export interface QuadroPilha {
  readonly route: string;
  readonly arg?: string;
}

/** Item do catálogo com a resposta sobreposta. */
export type ItemMerged = ItemCatalogo & Partial<RespostaItem>;
export type Respostas = Record<string, Partial<RespostaItem>>;

/** IMGQ de uma visita (ciclo) da unidade, para o painel de evolução. */
export interface VisitaEvolucao {
  id: string; // avaliacaoId
  rotulo: string; // "1ª visita · Jun"
  imgq: number; // IMGQ ajustado do ciclo
  selo: ChaveSelo;
  delta: number | null; // variação vs a visita anterior
  atual: boolean; // é a avaliação selecionada?
}

interface EstadoPersistido {
  auth: UsuarioAutenticado | null;
  token: string | null;
  /** Sessão da conta `unidade` (login único; view do Plano de Ação). */
  unidadeSession: SessaoUnidade | null;
  avaliacaoId: string | null;
  tab: Aba;
  stack: QuadroPilha[];
  momento: Momento;
  responsesPorAvaliacao: Record<string, Respostas>;
  // Offline-first: guarda as avaliações já sincronizadas (com o ciclo) para que,
  // ao reabrir o app sem rede, o ciclo aberto continue visível em vez de cair no
  // estado "Nenhum ciclo aberto".
  avaliacoes: AvaliacaoRemota[];
  // Edições locais da supervisão (AUDIN) por avaliação — offline-first.
  supervisaoPorAvaliacao: Record<string, SupervisaoData>;
  // Escopo (setores existentes na unidade) por avaliação/ciclo — offline-first.
  escopoPorAvaliacao: Record<string, EscopoData>;
}

/** Todos os 18 códigos de setor do instrumento (05A..05R). */
const SETORES_TODOS: readonly string[] = SETORES.map((s) => s.code);

/** Nº de itens aplicáveis (universo avaliável) dado um conjunto de setores ativos. */
function contarAplicaveis(setoresAtivos: readonly string[]): number {
  return setoresAtivos.reduce((n, c) => n + (ITENS_POR_SETOR[c] ?? 0), 0);
}

/** Respostas vindas do servidor (somente leitura) para uma avaliação. */
function respostasPuxadas(av: AvaliacaoRemota | undefined): Respostas {
  const r: Respostas = {};
  for (const x of av?.respostas ?? []) {
    r[x.itemId] = {
      mi: (x.mi ?? null) as RespostaItem["mi"],
      mf: (x.mf ?? null) as RespostaItem["mf"],
      evidenciaMi: x.evidenciaMi ?? undefined,
      evidenciaMf: x.evidenciaMf ?? undefined,
      tipoMi: (x.tipoMi ?? null) as RespostaItem["tipoMi"],
      tipoMf: (x.tipoMf ?? null) as RespostaItem["tipoMf"],
      fonteMi: x.fonteMi ?? undefined,
      fonteMf: x.fonteMf ?? undefined,
    };
  }
  return r;
}

/**
 * Funde as respostas do SERVIDOR com as edições LOCAIS campo a campo.
 *
 * Antes o merge era por ITEM (`{...servidor, ...locais}`): a entrada local —
 * que guarda só os campos tocados NESTE aparelho — substituía a do servidor
 * inteira. Num tablet sem histórico local do item (storage do PWA expirado,
 * "limpar dados", reinstalação, outro aparelho, outro avaliador), o PRIMEIRO
 * toque no item apagava da tela tudo o que veio do servidor: escolher o "tipo
 * de constatação" zerava a maturidade e o registro da evidência, e só a foto
 * sobrevivia — porque foto mora em outra tabela, não na resposta.
 *
 * O dado no servidor estava intacto (o push é parcial e o backend mescla desde
 * `62d8e09`); era a TELA que mentia — e o avaliador refazia a avaliação por
 * cima. Esta função é a metade cliente daquela mesma correção.
 */
export function mesclarRespostas(servidor: Respostas, locais: Respostas | undefined): Respostas {
  if (!locais) {
    return servidor;
  }
  const saida: Respostas = { ...servidor };
  for (const [itemId, local] of Object.entries(locais)) {
    const base = saida[itemId];
    if (!base) {
      saida[itemId] = local;
      continue;
    }
    // `undefined` ⇒ campo não tocado localmente → preserva o do servidor.
    // `null`/`""` ⇒ limpeza deliberada do avaliador → sobrescreve.
    const item: Record<string, unknown> = { ...base };
    for (const [campo, valor] of Object.entries(local)) {
      if (valor !== undefined) {
        item[campo] = valor;
      }
    }
    saida[itemId] = item as Partial<RespostaItem>;
  }
  return saida;
}

/**
 * Itens (catálogo + respostas) de uma avaliação qualquer — servidor + edições
 * locais. `overrides` aplica a decisão do supervisor (item → maturidade que
 * prevalece) sobre `mi`, refletindo no IMGQ.
 */
export function itensDaAvaliacao(
  av: AvaliacaoRemota,
  locais: Respostas | undefined,
  overrides: Record<string, NivelMaturidade> = {},
): ItemMerged[] {
  const resp = mesclarRespostas(respostasPuxadas(av), locais);
  return CATALOGO.map((it) => {
    const merged = { ...it, ...(resp[it.id] ?? {}) };
    const ov = overrides[it.id];
    return (ov ? { ...merged, mi: ov } : merged) as ItemMerged;
  });
}

/** Overrides do supervisor (item → maturidade que prevalece) de um ciclo: local em edição ?? servidor. */
function overridesDoCiclo(
  av: AvaliacaoRemota,
  locais: Record<string, SupervisaoData>,
): Record<string, NivelMaturidade> {
  const sup = locais[av.id] ?? av.supervisao?.dados;
  return sup ? overridesSupervisor(sup) : {};
}

/** IMGQ ajustado de um ciclo, ancorando o QACI/IMS REAIS só na última visita. */
function imgqComMonitoramento(av: AvaliacaoRemota, its: ItemMerged[], ultimo: boolean): ResultadoImgq {
  const qaci = av.qaciFinal ?? 0;
  const ims = av.ims ?? 0;
  const temMon = ultimo && (qaci > 0 || ims > 0);
  return engine.imgq(its as ItemAvaliado[], "mi", temMon ? qaci : undefined, temMon ? ims : undefined);
}

/** Avaliação editável: ciclo aberto e a data atual dentro da janela. */
function avaliacaoEditavel(av: AvaliacaoRemota | undefined): boolean {
  const c = av?.ciclo;
  if (!c || c.status !== "aberto") {
    return false;
  }
  const agora = Date.now();
  if (c.dataInicio && agora < Date.parse(c.dataInicio)) {
    return false;
  }
  if (c.dataFim && agora > Date.parse(c.dataFim)) {
    return false;
  }
  return true;
}

export interface UsuariosApi {
  listar: () => Promise<UsuarioPublico[]>;
  criar: (dto: CriarUsuarioDTO) => Promise<UsuarioPublico>;
  atualizar: (id: string, dto: AtualizarUsuarioDTO) => Promise<UsuarioPublico>;
  redefinirSenha: (id: string, senha: string) => Promise<void>;
  senhaProvisoria: (id: string) => Promise<{ senha: string | null }>;
}

export interface CiclosApi {
  listar: () => Promise<CicloPublico[]>;
  abrir: (dto: AbrirCicloDTO) => Promise<{ id: string; avaliacaoId: string }>;
  encerrar: (id: string) => Promise<void>;
  /** Edita datas/status do ciclo (estender prazo, reabrir, encerrar). */
  editar: (id: string, dto: EditarCicloDTO) => Promise<void>;
  /** Edita o escopo (setores) do ciclo — gestão, independe de aberto/encerrado. */
  salvarEscopo: (avaliacaoId: string, escopo: EscopoData) => Promise<void>;
  /** Resultado consolidado (IMGR) das respostas do formulário público. */
  resultado: (cicloId: string) => Promise<DashboardImgr>;
}

/** Matriz da unidade (roster esperado): ler/gravar (admin/coord). */
export interface MatrizApi {
  obter: (unidadeId: string) => Promise<MatrizUnidade | null>;
  salvar: (unidadeId: string, dto: MatrizUploadDTO) => Promise<void>;
  /** Unidades que já têm quadro no sistema, com a data do último envio. */
  listar: () => Promise<ResumoMatrizUnidade[]>;
}

/** Migração de catálogo (Workbench): decidir (avaliadores) e executar (presidente/admin). */
export interface MigracaoApi {
  ingerir: (proposta: unknown) => Promise<{ loteId: string }>;
  listar: () => Promise<LoteRemoto[]>;
  lote: (id: string) => Promise<LoteDetalheRemoto>;
  impacto: (itemId: string) => Promise<ImpactoRemoto>;
  decidir: (
    loteId: string,
    itemId: string,
    decisao: DecisaoMigracaoValor,
    codigoDestino: string | null,
  ) => Promise<void>;
  aprovar: (id: string) => Promise<void>;
  preflight: (id: string) => Promise<PreflightRemoto>;
  executar: (id: string) => Promise<ResultadoExecucaoRemoto>;
  reverter: (id: string) => Promise<ResultadoExecucaoRemoto["resumo"]>;
  manifesto: (id: string) => Promise<ManifestoRemoto | null>;
}

/** Estado visível da sincronização — alimenta o indicador/erro na UI. */
export interface SyncStatus {
  readonly estado: "ocioso" | "sincronizando" | "erro" | "retido";
  readonly mensagem?: string;
}

export interface AppState {
  auth: UsuarioAutenticado | null;
  /** Sessão da conta `unidade` (login único; o app mostra só o Plano de Ação). */
  unidadeSession: SessaoUnidade | null;
  /** JWT da sessão (usado por telas que chamam a API direto, ex.: Plano de Ação). */
  token: string | null;
  /** A última saída foi por sessão expirada (401), não logout manual — a tela de
   *  login usa isto para avisar "sua sessão expirou". */
  sessaoExpirada: boolean;
  online: boolean;
  pendingSync: number;
  /** Situação do último sync (para mostrar progresso/erro em vez de falhar mudo). */
  syncStatus: SyncStatus;
  tab: Aba;
  stack: QuadroPilha[];
  momento: Momento;
  items: ItemMerged[];
  /** Itens dentro do escopo (setores ativos) — base de cálculo/relatório. */
  itensEscopo: ItemMerged[];
  /** Itens do escopo SEM overrides do supervisor — base da supervisão (preserva a nota original). */
  itensEscopoSup: ItemMerged[];
  /** Escopo (setores existentes na unidade) da avaliação atual. */
  escopo: EscopoData;
  /** Alvo efetivo da amostra cega: `min(28, nAplicáveis)` (doc 17b §3.5). */
  amostraAlvo: number;
  /** Checklist do escopo concluído (todo item aplicável com maturidade + evidência/NA). */
  checklistCompleto: boolean;
  /** (De)seleciona um setor do escopo (obrigatórios travados). */
  alternarSetor: (setor: string) => void;
  /** Confirma o escopo e libera o checklist. */
  confirmarEscopo: () => void;
  imgq: ResultadoImgq;
  qaci: number;
  ims: number;
  /** Avaliações existentes (de qualquer unidade) que o usuário pode VER. */
  avaliacoes: AvaliacaoRemota[];
  avaliacaoAtual: AvaliacaoRemota | undefined;
  /** IMGQ por visita (ciclo) da unidade atual + delta — painel de evolução. */
  evolucao: VisitaEvolucao[];
  /** Panorama comparativo entre unidades (só gestão/AUDIN; `null` caso contrário). */
  panoramaRede: Panorama | null;
  /** Pode ver o Panorama da Rede (gestão + AUDIN) — gate da tela comparativa. */
  podeVerPanorama: boolean;
  /** Supervisão AUDIN da avaliação atual (servidor + edições locais). */
  supervisao: SupervisaoData;
  /** Editam a supervisão: audin + gestão (admin, coord, presidente). Só o PAPEL. */
  podeSupervisionar: boolean;
  /**
   * Capacidade REAL de editar a supervisão = papel + ciclo selecionado. É o que
   * a UI deve consultar: sem ciclo, `editarSupervisao` descarta a mutação, então
   * marcar a tela como editável seria mentira (incidente de 17/ago/2026).
   */
  podeEditarSupervisao: boolean;
  editarSupervisao: (mut: (d: SupervisaoData) => SupervisaoData) => void;
  enviarSupervisao: (validar?: boolean) => Promise<string | null>;
  /** Libera a amostra cega + sorteio novo + reset das contestações (repetível). */
  liberarAmostra: () => void;
  /** Mostra o botão liberar/refazer: pode supervisionar E a trava estrita não passa. */
  podeLiberarAmostra: boolean;
  /** Edição liberada só quando a avaliação atual tem ciclo aberto e na janela. */
  editavel: boolean;
  /** Recupera respostas presas no dispositivo: re-enfileira o ciclo atual e sincroniza. */
  reenviarRespostasLocais: () => void;
  /** Há respostas locais no ciclo atual (habilita o reenvio manual). */
  temRespostasLocais: boolean;
  selecionar: (avaliacaoId: string) => void;
  recarregar: () => Promise<void>;
  ciclosApi: CiclosApi;
  matrizApi: MatrizApi;
  migracaoApi: MigracaoApi;
  signIn: (matricula: string, senha: string) => Promise<string | null>;
  trocarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>;
  logout: () => void;
  sync: () => Promise<void>;
  switchTab: (t: Aba) => void;
  go: (route: string, arg?: string) => void;
  back: () => void;
  setMomento: (m: Momento) => void;
  patch: (itemId: string, patch: Partial<RespostaItem>) => void;
  evidencias: Record<string, EvidenciaRemota[]>;
  evidenciasPendentes: Record<string, EvidenciaPendente[]>;
  enviarEvidencia: (itemId: string, momento: Momento, arquivo: File) => Promise<string | null>;
  excluirEvidencia: (ev: EvidenciaRemota) => Promise<string | null>;
  substituirEvidencia: (ev: EvidenciaRemota, arquivo: File) => Promise<string | null>;
  removerEvidenciaPendente: (localId: string) => Promise<void>;
  usuariosApi: UsuariosApi;
}

/**
 * Garante a amostra cega. Ela é DERIVADA dos itens JÁ avaliados (doc 09 §9.5) E
 * restritos ao ESCOPO (setores ativos, doc 17b §3.5): enquanto INTOCADA pelo
 * Supervisor, só é gerada quando o checklist do escopo está COMPLETO (todos os
 * itens aplicáveis avaliados) — garantindo aleatoriedade sobre um checklist
 * concluído; abaixo disso fica vazia (a aba é gated). O sorteio considera apenas
 * itens com maturidade 2-4 (ver `gerarAmostraCega`). A partir do 1º preenchimento
 * do Supervisor, é preservada (nunca descarta dados). Sempre migra o blob legado
 * (encaminhamento/Manter → status/Validar) na leitura.
 */
function comAmostraCega(
  dados: SupervisaoData,
  avaliacaoId: string,
  itensAvaliados: readonly ItemAvaliavel[],
  setoresAtivos: ReadonlySet<string>,
  alvo: number,
  checklistCompleto: boolean,
): SupervisaoData {
  const migrado = migrarSupervisao(dados);
  // Sorteou uma vez, congelou. Antes a guarda era "nenhum item re-performado", o
  // que re-sorteava a amostra JÁ EXISTENTE sempre que o pool mudava — e o pool
  // muda o tempo todo, porque deriva de `itensAvaliados` e dos setores ativos,
  // que o avaliador segue mexendo. O blob local mascarava isso; sem ele, a
  // amostra remexeria sob o supervisor no meio do trabalho. Refazer o sorteio é
  // ato explícito: `resetarAmostra` esvazia o array e incrementa `amostraRev`.
  if (migrado.amostra.length > 0) {
    return migrado;
  }
  const amostra =
    checklistCompleto && alvo > 0
      ? gerarAmostraCega(itensAvaliados, {
          seed: seedAmostra(avaliacaoId, migrado.amostraRev),
          setoresAtivos,
          limite: alvo,
        })
      : [];
  return { ...migrado, amostra };
}

export function useAppState(
  store: KeyValueStore = storePadrao(),
  api: ApiClient = apiClient,
  fila: EvidenciaQueue = filaPadrao(),
): AppState {
  const outbox = useMemo(() => new SyncOutbox(store), [store]);
  // Fila DURÁVEL da supervisão: chave própria, fora de `caint-mobile-v2`, para
  // sobreviver ao logout e ao recarregar (doc 20 §I1/§I5).
  const filaSup = useMemo(() => new SupervisaoPendente(store), [store]);
  const inicial = useMemo<EstadoPersistido>(() => {
    // Modelo de visitas como ciclos: cada ciclo guarda uma maturidade por item
    // em `mi`. O app opera sempre em "mi"; a "visita" é o ciclo escolhido.
    const padrao: EstadoPersistido = {
      auth: null,
      token: null,
      unidadeSession: null,
      avaliacaoId: null,
      tab: "inicio",
      stack: [],
      momento: "mi",
      responsesPorAvaliacao: {},
      avaliacoes: [],
      supervisaoPorAvaliacao: {},
      escopoPorAvaliacao: {},
    };
    // Merge sobre o padrão: sessões antigas não têm as chaves novas — sem isso,
    // campos adicionados depois ficam `undefined` e quebram o app (tela branca).
    return { ...padrao, ...store.read<Partial<EstadoPersistido>>(CHAVE, {}) };
  }, [store]);

  const [auth, setAuth] = useState(inicial.auth);
  const [token, setToken] = useState<string | null>(inicial.token);
  const [unidadeSession, setUnidadeSession] = useState<SessaoUnidade | null>(inicial.unidadeSession ?? null);
  const [sessaoExpirada, setSessaoExpirada] = useState(false);
  const [avaliacaoId, setAvaliacaoId] = useState<string | null>(inicial.avaliacaoId);
  const [tab, setTab] = useState<Aba>(inicial.tab);
  const [stack, setStack] = useState<QuadroPilha[]>(inicial.stack);
  // Sempre "mi": cada visita é um ciclo com uma maturidade por item. Ignora o
  // valor persistido (legado "mf") para migrar sessões antigas automaticamente.
  const [momento, setMomento] = useState<Momento>("mi");
  const [responsesPorAvaliacao, setResponses] = useState<Record<string, Respostas>>(
    inicial.responsesPorAvaliacao,
  );
  const [avaliacoes, setAvaliacoes] = useState<AvaliacaoRemota[]>(inicial.avaliacoes);
  // Espelho reativo da fila. A VERDADE durável é `filaSup`; este estado só existe
  // para o React re-renderizar. Uma cópia só, com dono — não há dois lugares que
  // precisem concordar entre si.
  const [pendentes, setPendentes] = useState<Record<string, EntradaPendente>>(() =>
    Object.fromEntries(filaSup.todas().map((e) => [e.avaliacaoId, e])),
  );
  // Migração única do formato antigo: o blob local vivia dentro de
  // `caint-mobile-v2`, sem dono e sem revisão. Entra na fila marcado `legado` e
  // NÃO sobe sozinho — algumas dessas cópias são trabalho real não enviado,
  // outras são cache velho que sobrescreveria dado bom. Quem decide é a pessoa.
  useEffect(() => {
    const antigas = inicial.supervisaoPorAvaliacao ?? {};
    if (Object.keys(antigas).length === 0 || !auth) {
      return;
    }
    for (const [avaliacaoId, dados] of Object.entries(antigas)) {
      if (filaSup.porAvaliacao(avaliacaoId)) {
        continue; // já migrada
      }
      filaSup.editar(avaliacaoId, dados, { id: auth.id, nome: auth.nome }, null);
      filaSup.marcar(avaliacaoId, "legado");
    }
    setPendentes(Object.fromEntries(filaSup.todas().map((e) => [e.avaliacaoId, e])));
  }, [auth, filaSup, inicial.supervisaoPorAvaliacao]);

  /**
   * Descarta a cópia local quando o servidor já a alcançou (versão >= a nossa).
   *
   * Sem isto a entrada ficaria pendurada para sempre depois de confirmada, e o
   * device voltaria a mascarar o servidor — o defeito que a fila veio consertar.
   * Roda a cada pull, que é quando a versão do servidor muda.
   */
  useEffect(() => {
    for (const a of avaliacoes) {
      const e = filaSup.porAvaliacao(a.id);
      if (e && !suja(e) && (e.versaoBase ?? 0) <= (a.supervisao?.versao ?? 0)) {
        filaSup.descartar(a.id);
      }
    }
    setPendentes(Object.fromEntries(filaSup.todas().map((e) => [e.avaliacaoId, e])));
  }, [avaliacoes, filaSup]);

  const recarregarPendentes = useCallback(() => {
    setPendentes(Object.fromEntries(filaSup.todas().map((e) => [e.avaliacaoId, e])));
  }, [filaSup]);
  const authIdRef = useRef<string | null>(null);
  /**
   * Contador ÚNICO das pendências: checklist + supervisão. Fica num callback só
   * (e lê o usuário por ref) para não espalhar `auth`/`filaSup` pelas listas de
   * dependência de meia dúzia de hooks — foi o que a regra de exaustividade
   * acusou quando isto estava inline.
   */
  useEffect(() => {
    authIdRef.current = auth?.id ?? null;
  }, [auth]);
  const atualizarPendencias = useCallback(() => {
    const id = authIdRef.current;
    setPendingSync(outbox.totalPendente() + (id ? filaSup.totalPendente(id) : 0));
  }, [outbox, filaSup]);
  /**
   * Blob local a EXIBIR — enquanto o servidor não tiver a nossa gravação.
   *
   * Perguntar só "está suja?" fazia o campo reverter para o texto antigo no
   * intervalo entre confirmar o envio e o pull trazer a gravação de volta.
   */
  const localVigente = useCallback(
    (id: string, versaoServidor: number): SupervisaoData | undefined => {
      const e = pendentes[id];
      return blobVigente(e, versaoServidor) === "local" ? e?.dados : undefined;
    },
    [pendentes],
  );
  /** Mapa "id → blob local vigente", no formato que `overridesDoCiclo` espera. */
  const supervisaoPorAvaliacao = useMemo<Record<string, SupervisaoData>>(() => {
    const versaoDe = new Map(avaliacoes.map((a) => [a.id, a.supervisao?.versao ?? 0]));
    const m: Record<string, SupervisaoData> = {};
    for (const e of Object.values(pendentes)) {
      if (blobVigente(e, versaoDe.get(e.avaliacaoId) ?? 0) === "local") {
        m[e.avaliacaoId] = e.dados;
      }
    }
    return m;
  }, [pendentes, avaliacoes]);
  const [escopoPorAvaliacao, setEscopoLocal] = useState<Record<string, EscopoData>>(
    inicial.escopoPorAvaliacao ?? {},
  );
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pendingSync, setPendingSync] = useState(() => outbox.totalPendente());
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ estado: "ocioso" });
  const [evidencias, setEvidencias] = useState<Record<string, EvidenciaRemota[]>>({});
  const [evidenciasPendentes, setEvidenciasPendentes] = useState<Record<string, EvidenciaPendente[]>>({});

  useEffect(() => {
    store.write<EstadoPersistido>(CHAVE, {
      auth,
      token,
      unidadeSession,
      avaliacaoId,
      tab,
      stack,
      momento,
      responsesPorAvaliacao,
      avaliacoes,
      // `supervisaoPorAvaliacao` saiu daqui: mora na fila durável, com chave
      // própria, para sobreviver ao logout e ao "limpar dados do app".
      supervisaoPorAvaliacao: {},
      escopoPorAvaliacao,
    });
  }, [
    store,
    auth,
    token,
    unidadeSession,
    avaliacaoId,
    tab,
    stack,
    momento,
    responsesPorAvaliacao,
    avaliacoes,
    escopoPorAvaliacao,
  ]);

  const avaliacaoAtual = useMemo(
    () => avaliacoes.find((a) => a.id === avaliacaoId),
    [avaliacoes, avaliacaoId],
  );
  // Visualizador (gerência) é somente leitura — nunca edita, mesmo com ciclo aberto.
  const editavel = useMemo(
    () => !!auth && !papelSoLeitura(auth.papel) && avaliacaoEditavel(avaliacaoAtual),
    [auth, avaliacaoAtual],
  );

  // Notas do avaliador (merge: puxadas do servidor + edições locais). Computadas
  // ANTES da supervisão para alimentar a amostra cega sem criar ciclo de
  // dependência com os overrides do supervisor (que dependem da amostra).
  const respostas = useMemo<Respostas>(
    () => mesclarRespostas(respostasPuxadas(avaliacaoAtual), responsesPorAvaliacao[avaliacaoId ?? ""]),
    [avaliacaoAtual, responsesPorAvaliacao, avaliacaoId],
  );
  // Itens já avaliados (nota do avaliador preenchida) — base e gating da amostra.
  const itensAvaliados = useMemo<ItemAvaliavel[]>(
    () =>
      CATALOGO.filter((it) => notaPreenchida(respostas[it.id]?.mi)).map((it) => ({
        id: it.id,
        peso: it.peso,
        p: it.p,
        i: it.i,
        setor: it.sector,
        mi: respostas[it.id]?.mi as NivelMaturidade,
      })),
    [respostas],
  );

  // Ciclos (visitas) da unidade, em ordem cronológica — base p/ herdar o escopo.
  const ciclosUnidade = useMemo(() => {
    const u = avaliacaoAtual?.unidadeId;
    if (!u) {
      return [] as AvaliacaoRemota[];
    }
    return avaliacoes
      .filter((a) => a.unidadeId === u)
      .sort((a, b) => (a.ciclo?.dataInicio ?? "").localeCompare(b.ciclo?.dataInicio ?? ""));
  }, [avaliacoes, avaliacaoAtual?.unidadeId]);

  // Sugestão de escopo para um ciclo novo: herda o ciclo anterior da mesma
  // unidade (se já tiver escopo salvo); senão sugere TODOS os setores (editável).
  const escopoSugerido = useMemo<string[]>(() => {
    for (let i = ciclosUnidade.length - 1; i >= 0; i--) {
      const c = ciclosUnidade[i];
      if (!c || c.id === avaliacaoId) {
        continue;
      }
      // Edição local pendente tem precedência sobre o escopo já sincronizado.
      const e = escopoPorAvaliacao[c.id] ?? c.escopo;
      if (e?.setoresAtivos?.length) {
        return [...e.setoresAtivos];
      }
    }
    return [...SETORES_TODOS];
  }, [ciclosUnidade, avaliacaoId, escopoPorAvaliacao]);

  // Escopo EFETIVO da avaliação atual — last-write-wins por `escopoConfirmadoEm`
  // entre a edição local (offline) e o escopo do servidor (ex.: gestor editou na
  // página de Ciclos). Sem isso, um local antigo mascararia a edição da gestão.
  // Doc 17b §4/§4b.
  const escopo = useMemo<EscopoData>(
    () =>
      resolverEscopo(
        escopoPorAvaliacao[avaliacaoId ?? ""],
        avaliacaoAtual?.escopo,
        itensAvaliados.length > 0,
        escopoSugerido,
        SETORES_TODOS,
      ),
    [escopoPorAvaliacao, avaliacaoId, avaliacaoAtual?.escopo, itensAvaliados.length, escopoSugerido],
  );

  const setoresAtivosSet = useMemo(() => new Set(escopo.setoresAtivos), [escopo.setoresAtivos]);
  // Universo avaliável (itens em setores ativos) e alvo efetivo da amostra cega.
  const nAplicaveis = useMemo(() => contarAplicaveis(escopo.setoresAtivos), [escopo.setoresAtivos]);
  const amostraAlvo = useMemo(() => tamanhoAmostraEfetivo(nAplicaveis), [nAplicaveis]);
  // Checklist do escopo "concluído o bastante": ≥ LIMIAR_CHECKLIST_COMPLETO (90%)
  // dos itens aplicáveis com maturidade + evidência (NA dispensa evidência). Só
  // então a amostra cega é gerada — aleatoriedade sobre um checklist quase
  // concluído, sem o supervisor esperar o último item (doc 09 §9.5).
  const checklistCompletoEstrito = useMemo(() => {
    if (nAplicaveis === 0) return false;
    let completos = 0;
    for (const it of CATALOGO) {
      if (!setoresAtivosSet.has(it.sector)) continue;
      const r = respostas[it.id];
      if (itemCompleto(r?.mi, r?.evidenciaMi)) completos++;
    }
    return checklistLiberado(completos, nAplicaveis);
  }, [respostas, setoresAtivosSet, nAplicaveis]);
  // Trava EFETIVA da amostra cega: a estrita OU a liberação manual da Supervisão
  // (flag por ciclo no escopo). O escopo é independente do blob da supervisão, o
  // que evita dependência circular com `comAmostraCega`.
  const checklistCompleto = checklistCompletoEstrito || (escopo.amostraLiberada === true && nAplicaveis > 0);

  // Supervisão AUDIN da avaliação atual: edição local tem precedência sobre o
  // que veio do servidor (offline-first). Editam: audin + gestão (admin, coord,
  // presidente); os demais papéis só leem.
  const podeSupervisionar = !!auth && PAPEIS_SUPERVISAO.has(auth.papel);
  // Base = o que veio do servidor (ou vazio). Para o Supervisor, a amostra
  // (Estrato A) é gerada AUTOMATICAMENTE por ciclo (semente = id da avaliação),
  // sem clique — derivada, então não há shadow de edições antes do pull.
  const supervisaoBase = useMemo<SupervisaoData>(() => {
    const servidor = avaliacaoAtual?.supervisao?.dados ?? supervisaoVazia();
    return podeSupervisionar && avaliacaoId
      ? comAmostraCega(
          servidor,
          avaliacaoId,
          itensAvaliados,
          setoresAtivosSet,
          amostraAlvo,
          checklistCompleto,
        )
      : servidor;
  }, [
    avaliacaoAtual,
    podeSupervisionar,
    avaliacaoId,
    itensAvaliados,
    setoresAtivosSet,
    amostraAlvo,
    checklistCompleto,
  ]);
  // Edição local tem precedência; também normaliza a amostra (migra tamanho antigo).
  const supervisao = useMemo<SupervisaoData>(() => {
    const local = localVigente(avaliacaoId ?? "", avaliacaoAtual?.supervisao?.versao ?? 0);
    if (!local) {
      return supervisaoBase;
    }
    const merged =
      podeSupervisionar && avaliacaoId
        ? comAmostraCega(local, avaliacaoId, itensAvaliados, setoresAtivosSet, amostraAlvo, checklistCompleto)
        : local;
    // Anti-masking (contestações): se a amostra LOCAL está intocada (nenhuma
    // re-performance cega) mas o SERVIDOR já trouxe uma amostra preenchida
    // (matSup/status/etc.), adota a do servidor. Sem isso, um cache local antigo
    // do device (ex.: aberto antes de o Supervisor preencher a amostra noutro
    // device) esconde as divergências e a aba Contestações fica vazia.
    // Exceção: um "refazer" local (amostraRev maior) é um sorteio NOVO deliberado
    // — não pode ser mascarado pela amostra antiga do servidor (rev menor).
    const intocada = (a: SupervisaoData["amostra"]) =>
      a.every((x) => x.matSup == null && !x.evidencia && !x.obs && !x.status);
    const revLocalMaisNova = (merged.amostraRev ?? 0) > (supervisaoBase.amostraRev ?? 0);
    if (!revLocalMaisNova && intocada(merged.amostra) && !intocada(supervisaoBase.amostra)) {
      return {
        ...merged,
        amostra: supervisaoBase.amostra,
        resolucoes: { ...(supervisaoBase.resolucoes ?? {}), ...(merged.resolucoes ?? {}) },
      };
    }
    return merged;
  }, [
    localVigente,
    avaliacaoAtual,
    avaliacaoId,
    podeSupervisionar,
    supervisaoBase,
    itensAvaliados,
    setoresAtivosSet,
    amostraAlvo,
    checklistCompleto,
  ]);
  // Marca edições pendentes da supervisão p/ auto-sync (espelha o checklist, que
  // sobe a cada `patch`). A supervisão é 1 blob; debounce + LWW pelo servidor.

  const editarSupervisao = useCallback(
    (mut: (d: SupervisaoData) => SupervisaoData) => {
      // Defesa em profundidade: afirma o ciclo ativo carregado (avaliacaoAtual.id).
      const avaliacaoId = exigirCiclo(avaliacaoAtual?.id, "editar supervisão");
      if (!avaliacaoId || !podeSupervisionar) {
        return;
      }
      const atual = localVigente(avaliacaoId, avaliacaoAtual?.supervisao?.versao ?? 0);
      const base = atual
        ? comAmostraCega(atual, avaliacaoId, itensAvaliados, setoresAtivosSet, amostraAlvo, checklistCompleto)
        : supervisaoBase;
      // Grava DURÁVEL antes de qualquer IO (doc 20 §I1). `versaoBase` é a versão
      // do servidor sobre a qual estamos editando — o backend recusa se ficar velha.
      const gravou = filaSup.editar(
        avaliacaoId,
        mut(base),
        { id: auth?.id ?? "", nome: auth?.nome ?? "" },
        avaliacaoAtual?.supervisao?.versao ?? null,
      );
      recarregarPendentes();
      if (!gravou) {
        // Cota cheia: o único caso em que a edição realmente não ficou guardada.
        setSyncStatus({
          estado: "erro",
          mensagem: "Sem espaço no aparelho para salvar. Libere espaço e refaça a última edição.",
        });
      }
    },
    [
      avaliacaoAtual,
      auth,
      podeSupervisionar,
      supervisaoBase,
      itensAvaliados,
      setoresAtivosSet,
      amostraAlvo,
      checklistCompleto,
      filaSup,
      localVigente,
      recarregarPendentes,
    ],
  );

  // Decisões do supervisor (divergências resolvidas como "supervisor") substituem
  // a maturidade do item no cálculo dos índices da avaliação supervisionada.
  const overridesSup = useMemo(() => overridesSupervisor(supervisao), [supervisao]);
  const items = useMemo<ItemMerged[]>(
    () =>
      CATALOGO.map((it) => {
        const base = { ...it, ...(respostas[it.id] ?? {}) };
        const ov = overridesSup[it.id];
        return ov ? { ...base, mi: ov } : base;
      }),
    [respostas, overridesSup],
  );
  // Itens DENTRO do escopo (setores ativos) — base de TODO cálculo/relatório.
  // Itens fora de escopo nem entram (equivale a NA agregada; doc 17b §3.4).
  const itensEscopo = useMemo<ItemMerged[]>(
    () => items.filter((it) => setoresAtivosSet.has(it.sector)),
    [items, setoresAtivosSet],
  );
  // Itens do escopo SEM os overrides do supervisor — base da própria supervisão
  // (amostra/contestação) para preservar a nota ORIGINAL do avaliador. Sem isto,
  // ao "Adotar supervisor" o override mudaria `mi` p/ a nota do supervisor e a
  // divergência sumiria (item ocultado da contestação).
  const itensEscopoSup = useMemo<ItemMerged[]>(
    () =>
      CATALOGO.map((it) => ({ ...it, ...(respostas[it.id] ?? {}) }) as ItemMerged).filter((it) =>
        setoresAtivosSet.has(it.sector),
      ),
    [respostas, setoresAtivosSet],
  );

  // QACI/IMS REAIS do ciclo ativo (revalidados no servidor por ciclo). `null` ⇒
  // ainda não coletados → 0 (não ancora o IMGQ). Sem seed estático disfarçado.
  const qaci = avaliacaoAtual?.qaciFinal ?? 0;
  const ims = avaliacaoAtual?.ims ?? 0;

  // QACI/IMS só ancoram na ÚLTIMA visita (ciclo final), não nas anteriores.
  const ehUltimoCiclo =
    ciclosUnidade.length === 0 || ciclosUnidade[ciclosUnidade.length - 1]?.id === avaliacaoAtual?.id;

  // Sem monitoramento (slate limpo) ou fora da última visita → sem ajuste.
  const imgq = useMemo<ResultadoImgq>(() => {
    const temMon = ehUltimoCiclo && (qaci > 0 || ims > 0);
    return engine.imgq(
      itensEscopo as ItemAvaliado[],
      momento,
      temMon ? qaci : undefined,
      temMon ? ims : undefined,
    );
  }, [itensEscopo, momento, qaci, ims, ehUltimoCiclo]);

  // Evolução entre visitas: IMGQ de cada ciclo da unidade + delta vs a anterior.
  const evolucao = useMemo<VisitaEvolucao[]>(() => {
    let anterior: number | null = null;
    return ciclosUnidade.map((a, i) => {
      // Override da supervisão daquele ciclo (decisões da contestação) no IMGQ.
      const itsTodos = itensDaAvaliacao(
        a,
        responsesPorAvaliacao[a.id],
        overridesDoCiclo(a, supervisaoPorAvaliacao),
      );
      // Recorte pelo escopo daquele ciclo (setor fora do escopo não conta) —
      // efetivo (local × servidor, LWW), igual à tela; compat = todos.
      const escopoCiclo = resolverEscopo(escopoPorAvaliacao[a.id], a.escopo, true, [], SETORES_TODOS);
      const ativosCiclo = new Set(escopoCiclo.setoresAtivos);
      const its = itsTodos.filter((it) => ativosCiclo.has(it.sector));
      const ultimo = i === ciclosUnidade.length - 1;
      const r = imgqComMonitoramento(a, its, ultimo);
      const mes = rotuloMes(a.ciclo?.dataInicio);
      const delta = anterior == null ? null : r.ajustado - anterior;
      anterior = r.ajustado;
      return {
        id: a.id,
        rotulo: `${i + 1}ª visita${mes ? ` · ${mes}` : ""}`,
        imgq: r.ajustado,
        selo: engine.selo(r.ajustado).chave,
        delta,
        atual: a.id === avaliacaoAtual?.id,
      };
    });
  }, [ciclosUnidade, responsesPorAvaliacao, escopoPorAvaliacao, avaliacaoAtual?.id, supervisaoPorAvaliacao]);

  // Panorama da rede (comparativo entre unidades) — só gestão/AUDIN. Uma entrada
  // por unidade (o ciclo mais recente), itens recortados pelo escopo do ciclo,
  // igual ao padrão de `evolucao`. `null` para quem não é gestão (a tela é gated).
  const podeVerPanorama = !!auth && papelEhGestao(auth.papel);
  const panoramaRede = useMemo<Panorama | null>(() => {
    if (!podeVerPanorama) {
      return null;
    }
    // Ciclo mais recente por unidade (por dataInicio; empate → atualizadoEm).
    const porUnidade = new Map<string, AvaliacaoRemota>();
    for (const a of avaliacoes) {
      const prev = porUnidade.get(a.unidadeId);
      const maisNova =
        !prev ||
        (a.ciclo?.dataInicio ?? "") > (prev.ciclo?.dataInicio ?? "") ||
        ((a.ciclo?.dataInicio ?? "") === (prev.ciclo?.dataInicio ?? "") &&
          a.atualizadoEm > prev.atualizadoEm);
      if (maisNova) {
        porUnidade.set(a.unidadeId, a);
      }
    }
    const entradas = [...porUnidade.values()].map((a) => {
      const its = itensDaAvaliacao(
        a,
        responsesPorAvaliacao[a.id],
        overridesDoCiclo(a, supervisaoPorAvaliacao),
      );
      const escopoA = resolverEscopo(escopoPorAvaliacao[a.id], a.escopo, true, [], SETORES_TODOS);
      const ativos = new Set(escopoA.setoresAtivos);
      return { av: a, itens: its.filter((it) => ativos.has(it.sector)) };
    });
    return panorama(entradas, avaliacoes.length);
  }, [podeVerPanorama, avaliacoes, responsesPorAvaliacao, escopoPorAvaliacao, supervisaoPorAvaliacao]);

  /** Drena a fila: sobe todo rascunho pendente do usuário logado. */
  const drenarSupervisao = useCallback(async (): Promise<void> => {
    if (!token || !auth) {
      return;
    }
    await filaSup.flush(auth.id, (e) =>
      pushSupervisao(api, token, e.avaliacaoId, e.dados, e.versaoBase, false),
    );
    recarregarPendentes();
    atualizarPendencias();
  }, [token, auth, api, filaSup, recarregarPendentes, atualizarPendencias]);

  const sync = useCallback(async () => {
    if (!token) {
      return;
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setSyncStatus({ estado: "erro", mensagem: "Sem conexão. Sincroniza ao reconectar." });
      return;
    }
    // A fila da supervisão anda junto: sem isto, ela seria durável e nunca drenaria.
    try {
      await drenarSupervisao();
    } catch {
      // Falha de rede aqui não deve abortar o flush do checklist.
    }
    if (outbox.totalPendente() === 0) {
      setSyncStatus({ estado: "ocioso" });
      return;
    }
    setSyncStatus({ estado: "sincronizando" });
    try {
      const r = await outbox.flush(api, token);
      atualizarPendencias();
      if (r.descartadas > 0) {
        setSyncStatus({
          estado: "erro",
          mensagem: `${r.descartadas} alteração(ões) com erro foram isoladas. Avise o suporte.`,
        });
      } else if (r.retidas > 0) {
        // NÃO é erro nem perda: aguardam o ciclo aceitar e sobem sozinhas.
        setSyncStatus({
          estado: "retido",
          mensagem: `${r.retidas} alteração(ões) aguardando o ciclo aceitar (encerrado ou fora da janela). Reenviaremos automaticamente.`,
        });
      } else {
        setSyncStatus({ estado: "ocioso" });
      }
    } catch (e) {
      atualizarPendencias();
      // Não engole mais o erro: a fila fica, mas o usuário vê o motivo.
      setSyncStatus({ estado: "erro", mensagem: mensagemAmigavel(e) });
    }
  }, [token, outbox, api, drenarSupervisao, atualizarPendencias]);

  /**
   * Drena a fila da supervisão. `validar` faz a validação explícita da avaliação
   * ATUAL — e exige conexão de propósito.
   *
   * Enfileirar uma validação seria inseguro: a pessoa valida offline, continua
   * editando (troca o parecer, mexe na amostra) e, ao reconectar, o sistema
   * selaria como oficial um rascunho que ela ainda estava mexendo, atribuindo o
   * ato a ela. Rascunho é offline; validar é ato de autoridade, com resposta do
   * servidor.
   */
  /** Validação explícita da avaliação atual — síncrona e só online. */
  const validarSupervisao = useCallback(
    async (avaliacaoId: string, token: string): Promise<string | null> => {
      const atual = filaSup.porAvaliacao(avaliacaoId);
      const dados = atual && suja(atual) ? atual.dados : supervisao;
      const r = await pushSupervisao(api, token, avaliacaoId, dados, atual?.versaoBase ?? null, true);
      if (!r.aplicado) {
        filaSup.descartar(avaliacaoId);
        return "Outro dispositivo salvou uma versão mais nova. A tela foi atualizada — confira e valide de novo.";
      }
      if (atual) {
        filaSup.confirmar(avaliacaoId, atual.rev, r.versao);
      }
      return null;
    },
    [api, filaSup, supervisao],
  );

  const enviarSupervisao = useCallback(
    async (validar = false): Promise<string | null> => {
      const avaliacaoId = avaliacaoAtual?.id;
      if (!avaliacaoId) {
        return "Selecione um ciclo antes de enviar a supervisão.";
      }
      if (!token || !podeSupervisionar || !auth) {
        return "Sem permissão ou sessão.";
      }
      if (validar && !online) {
        return "Você precisa estar online para validar a avaliação.";
      }
      try {
        const aviso = validar ? await validarSupervisao(avaliacaoId, token) : null;
        if (!validar) {
          await drenarSupervisao();
        }
        const avs = await api.pull(null, token);
        setAvaliacoes(avs);
        recarregarPendentes();
        atualizarPendencias();
        return aviso;
      } catch (e) {
        recarregarPendentes();
        return mensagemAmigavel(e);
      }
    },
    [
      token,
      auth,
      online,
      avaliacaoAtual,
      podeSupervisionar,
      api,
      validarSupervisao,
      drenarSupervisao,
      recarregarPendentes,
      atualizarPendencias,
    ],
  );

  // Auto-sync da supervisão: todo preenchimento sobe sozinho, como o checklist.
  // O gatilho agora é a FILA (durável), não um `useRef` que morria ao recarregar
  // a página — era por isso que uma edição offline nunca voltava a ser tentada.
  const sujasDoUsuario = auth ? filaSup.prontasPara(auth.id).length : 0;
  useEffect(() => {
    if (sujasDoUsuario === 0 || !online) {
      return;
    }
    const t = setTimeout(() => void enviarSupervisao(false), 1200);
    return () => clearTimeout(t);
  }, [sujasDoUsuario, online, enviarSupervisao]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const aoOnline = () => {
      setOnline(true);
      void sync();
    };
    const aoOffline = () => setOnline(false);
    window.addEventListener("online", aoOnline);
    window.addEventListener("offline", aoOffline);
    return () => {
      window.removeEventListener("online", aoOnline);
      window.removeEventListener("offline", aoOffline);
    };
  }, [sync]);

  // Flush periódico (30s): enquanto houver sessão e rede, força as pendências a
  // subir sozinhas — inclui as RETIDAS (reprocessa a cada flush), então uma edição
  // recusada por ciclo fora da janela é re-tentada até o ciclo aceitar.
  useEffect(() => {
    if (typeof window === "undefined" || !token) {
      return;
    }
    const id = window.setInterval(() => {
      if (navigator.onLine && outbox.totalPendente() > 0) {
        void sync();
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, [token, outbox, sync]);

  const fazerLogin = useCallback((u: UsuarioAutenticado) => {
    setAuth(u);
    setTab("inicio");
    setStack([]);
  }, []);

  const recarregarEvidencias = useCallback(
    async (tok: string, av: string | null) => {
      if (!av) {
        setEvidencias({});
        return;
      }
      try {
        const lista = await api.listarEvidencias(av, tok);
        const porItem: Record<string, EvidenciaRemota[]> = {};
        for (const e of lista) {
          const arr = porItem[e.itemId] ?? [];
          arr.push(e);
          porItem[e.itemId] = arr;
        }
        setEvidencias(porItem);
      } catch {
        // best-effort
      }
    },
    [api],
  );

  const carregarPendentes = useCallback(async () => {
    const todas = await fila.todas();
    const porItem: Record<string, EvidenciaPendente[]> = {};
    for (const p of todas) {
      const arr = porItem[p.itemId] ?? [];
      arr.push(p);
      porItem[p.itemId] = arr;
    }
    setEvidenciasPendentes(porItem);
  }, [fila]);

  // Evita drenagens concorrentes (intervalo + reconexão + troca de avaliação)
  // tentando enviar o mesmo item ao mesmo tempo (uploads duplicados).
  const drenandoRef = useRef(false);
  /**
   * Roda uma drenagem SOB A TRAVA. `null` = havia outra em curso e esta foi
   * dispensada — a fila é a mesma, então o item já está (ou estará, no próximo
   * tique) nas mãos da drenagem que roda. Propaga falha transitória para quem
   * chamou decidir o que dizer à pessoa.
   */
  const sobTrava = useCallback(async <T>(drenagem: () => Promise<T>): Promise<T | null> => {
    if (drenandoRef.current) {
      return null;
    }
    drenandoRef.current = true;
    try {
      return await drenagem();
    } finally {
      drenandoRef.current = false;
    }
  }, []);
  /** Drenagem COMPLETA da fila — o trabalho de fundo (intervalo, reconexão, login). */
  const drenarAgora = useCallback(
    (tok: string) => sobTrava(() => drenarEvidencias(fila, api, tok)),
    [sobTrava, fila, api],
  );
  const drenar = useCallback(
    async (tok: string, av: string | null) => {
      try {
        await drenarAgora(tok);
      } catch {
        // sem rede / falha transitória — pendentes ficam para a próxima tentativa
      }
      await carregarPendentes();
      await recarregarEvidencias(tok, av);
    },
    [drenarAgora, carregarPendentes, recarregarEvidencias],
  );

  // Evita PULLS concorrentes (intervalo + reconexão + visibilidade disparando ao
  // mesmo tempo) — sem isto, requisições se acumulam até ERR_INSUFFICIENT_RESOURCES.
  const puxandoRef = useRef(false);
  /** Re-pull das avaliações existentes (com ciclo) e seleção do alvo. */
  const recarregar = useCallback(async () => {
    if (!token || puxandoRef.current) {
      return;
    }
    puxandoRef.current = true;
    try {
      const avs = await api.pull(null, token);
      setAvaliacoes(avs);
      setAvaliacaoId((atual) => escolherAvaliacao(avs, atual));
    } catch {
      // best-effort
    } finally {
      puxandoRef.current = false;
    }
  }, [token, api]);

  useEffect(() => {
    void carregarPendentes();
  }, [carregarPendentes]);
  useEffect(() => {
    if (online && token) {
      void drenar(token, avaliacaoId);
    }
  }, [online, token, avaliacaoId, drenar]);

  // Carrega as evidências JÁ ENVIADAS (do servidor) da avaliação selecionada —
  // LEITURA, independente da fila de upload. Antes, ver evidências dependia do
  // `drenar` (envio), que pode ser pulado pelo guard de concorrência; no PC, o
  // gestor que troca de unidade podia ficar sem ver as fotos já enviadas. Agora
  // a visualização nunca depende do estado da fila de upload.
  useEffect(() => {
    if (token) {
      void recarregarEvidencias(token, avaliacaoId);
    }
  }, [token, avaliacaoId, recarregarEvidencias]);

  // Re-pull periódico das URLs de download (pré-assinadas, bucket privado). Sem
  // isto, ao deixar a tela aberta por horas as URLs expiram e as miniaturas
  // "caem" (quebram) até recarregar à mão. Renova bem antes da expiração do
  // backend (6 h) para que as imagens nunca sumam.
  // 4 h (a URL do backend vale 6 h — 2 h de folga). ANTES eram 30 min, e isso
  // custava caro por dois motivos: (1) cada re-pull gera URLs pré-assinadas
  // NOVAS, e como a assinatura entra na query string o navegador as trata como
  // outro endereço — o cache era descartado e TODAS as fotos da tela baixavam de
  // novo, 48× por dia; (2) cada `GET /evidencias` grava um evento
  // `evidencia.download` na trilha encadeada, então o polling virava ruído de
  // auditoria. O refresh ao reabrir a aba cobre o caso do aparelho que dormiu.
  useEffect(() => {
    if (typeof window === "undefined" || !token) {
      return;
    }
    const RENOVA_MS = 4 * 60 * 60 * 1000;
    let ultimaRenovacao = Date.now();
    const renovar = () => {
      ultimaRenovacao = Date.now();
      void recarregarEvidencias(token, avaliacaoId);
    };
    const id = window.setInterval(renovar, RENOVA_MS);
    // Ao voltar para a aba, só renova se as URLs estiverem perto de expirar —
    // sem isto, alternar de aba baixaria as fotos de novo a cada visita.
    const aoVoltar = () => {
      if (document.visibilityState === "visible" && Date.now() - ultimaRenovacao >= RENOVA_MS) {
        renovar();
      }
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [token, avaliacaoId, recarregarEvidencias]);

  // Total de evidências aguardando upload (todas as avaliações).
  const evidenciasPendentesTotal = useMemo(
    () => Object.values(evidenciasPendentes).reduce((n, arr) => n + arr.length, 0),
    [evidenciasPendentes],
  );

  // Retry periódico das EVIDÊNCIAS (fotos/PDF). Antes, a fila só drenava ao
  // capturar, ao reconectar e ao trocar de avaliação — então, se o envio imediato
  // falhasse (instabilidade do B2/rede) com o app ABERTO e ONLINE, a captura ficava
  // presa "na fila" indefinidamente, sem nova tentativa. Igual ao flush do outbox,
  // agora re-tenta sozinha enquanto houver pendência e rede (gate em pendência > 0
  // para não bater na API à toa).
  useEffect(() => {
    if (typeof window === "undefined" || !token || evidenciasPendentesTotal === 0) {
      return;
    }
    const id = window.setInterval(() => {
      if (navigator.onLine) {
        void drenar(token, avaliacaoId);
      }
    }, 20_000);
    return () => window.clearInterval(id);
  }, [token, avaliacaoId, drenar, evidenciasPendentesTotal]);

  // Re-pull das avaliações (ciclos) automaticamente — não só no login. Sem isto,
  // um usuário JÁ logado (sessão salva) carrega as avaliações do localStorage e
  // nunca re-puxa: quando o presidente abre um ciclo, esse usuário fica preso em
  // "Nenhum ciclo aberto" até deslogar e logar de novo. Atualiza ao montar com
  // sessão salva, ao reconectar e ao app voltar ao primeiro plano (PWA retomado).
  // Ao reconectar/voltar ao primeiro plano, também FORÇA a subida das pendências
  // na hora (não espera o intervalo) — "com internet, sempre sobe + atualiza".
  useEffect(() => {
    if (!token) {
      return;
    }
    if (online) {
      void recarregar();
      void sync();
    }
    if (typeof document === "undefined") {
      return;
    }
    const aoVisivel = () => {
      if (document.visibilityState === "visible") {
        void recarregar();
        void sync();
      }
    };
    document.addEventListener("visibilitychange", aoVisivel);
    return () => document.removeEventListener("visibilitychange", aoVisivel);
  }, [online, token, recarregar, sync]);

  // Pull periódico (30s): puxa updates do servidor mesmo sem trocar de tela — a
  // gestão vê alterações de outros dispositivos ao vivo e o avaliador recebe
  // reabertura/ajuste de janela sem recarregar à mão ("update do servidor").
  useEffect(() => {
    if (typeof window === "undefined" || !token) {
      return;
    }
    const id = window.setInterval(() => {
      if (navigator.onLine) {
        void recarregar();
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, [token, recarregar]);

  const hidratar = useCallback(
    async (tok: string, usuarioId?: string) => {
      try {
        const avs = await api.pull(null, tok);
        setAvaliacoes(avs);
        let alvo: string | null = null;
        setAvaliacaoId((atual) => {
          alvo = escolherAvaliacao(avs, atual);
          return alvo;
        });
        await outbox.flush(api, tok);
        // Login novo ⇒ token novo: o que parou em 401 volta a ser enviável.
        if (usuarioId) {
          filaSup.reativarPorSessao(usuarioId);
          setPendentes(Object.fromEntries(filaSup.todas().map((e) => [e.avaliacaoId, e])));
        }
        atualizarPendencias();
        await drenar(tok, alvo);
      } catch {
        // o login já valeu; sincroniza depois
      }
    },
    [api, outbox, drenar, filaSup, atualizarPendencias],
  );

  const signIn = useCallback(
    async (matricula: string, senha: string): Promise<string | null> => {
      try {
        const r = await api.login(matricula, senha);
        // Login ÚNICO: a conta de unidade usa a MESMA tela. Guarda uma sessão
        // separada (unidadeSession) e NÃO seta a sessão interna (auth/token) —
        // assim nenhum efeito interno (pull/sync, que dá 401/403 p/ unidade)
        // roda. O app então mostra só a view do Plano de Ação (por perfil).
        if (r.usuario.papel === "unidade") {
          setSessaoExpirada(false);
          setUnidadeSession({
            token: r.token,
            nome: r.usuario.nome,
            cargo: r.usuario.cargoUnidade ?? null,
          });
          return null;
        }
        setSessaoExpirada(false);
        setToken(r.token);
        fazerLogin(paraUsuario(r));
        await hidratar(r.token, r.usuario.id);
        return null;
      } catch (e) {
        return mensagemDeLogin(e);
      }
    },
    [api, fazerLogin, hidratar],
  );

  const logout = useCallback(() => {
    setAuth(null);
    setToken(null);
    setUnidadeSession(null);
    setStack([]);
    setAvaliacoes([]);
    // A supervisão pendente NÃO é apagada: ela vive na fila durável, com dono.
    // Antes o logout destruía trabalho de campo que ainda não tinha subido — e o
    // "Limpar Dados do Safari" recomendado no fix do iOS fazia o mesmo estrago.
    // A guarda de autoria impede que o próximo usuário envie o que não é dele.
    setEscopoLocal({});
  }, []);

  // Sessão expirada/ inválida: um 401 numa chamada AUTENTICADA (token velho após
  // reinício do backend ou passados os 7d) desloga e sinaliza para o login avisar,
  // em vez de deixar "Não autorizado" solto em cada tela. Registrado no cliente
  // de API (um único ponto pega todas as rotas: usuários, ciclos, IMGR, plano…).
  const expirarSessao = useCallback(() => {
    setSessaoExpirada(true);
    logout();
  }, [logout]);

  useEffect(() => {
    api.aoNaoAutorizado?.(expirarSessao);
    return () => api.aoNaoAutorizado?.(() => {});
  }, [api, expirarSessao]);
  const switchTab = useCallback((t: Aba) => {
    setStack([]);
    setTab(t);
  }, []);
  const go = useCallback((route: string, arg?: string) => {
    setStack((st) => [...st, { route, arg }]);
  }, []);
  const back = useCallback(() => {
    setStack((st) => st.slice(0, -1));
  }, []);
  const selecionar = useCallback((id: string) => setAvaliacaoId(id), []);

  // Edita o escopo da avaliação atual (base = local salvo migrado, ou o derivado).
  const editarEscopo = useCallback(
    (mut: (e: EscopoData) => EscopoData) => {
      // Defesa em profundidade: afirma o ciclo ativo carregado (avaliacaoAtual.id).
      const avaliacaoId = exigirCiclo(avaliacaoAtual?.id, "editar escopo");
      if (!avaliacaoId) {
        return;
      }
      setEscopoLocal((m) => {
        // Parte do escopo EFETIVO (local × servidor, LWW) — nunca de um local
        // possivelmente desatualizado, senão reverteria uma edição da gestão.
        // Toda edição local é uma NOVA escrita: carimba `escopoConfirmadoEm =
        // agora` para vencer o desempate LWW de `resolverEscopo` (empate ⇒
        // servidor vence). Sem isto, a edição herda o timestamp do servidor,
        // empata e é mascarada — o setor (des)marcado "volta" sozinho e a
        // alteração nunca chega ao checklist. Só o LWW usa este campo; o gate do
        // checklist é o boolean `escopoConfirmado`, preservado pela mutação.
        return { ...m, [avaliacaoId]: { ...mut(escopo), escopoConfirmadoEm: new Date().toISOString() } };
      });
    },
    [avaliacaoAtual, escopo],
  );

  /**
   * (De)seleciona um setor no escopo. Obrigatórios (05A/05C) não podem ser
   * desmarcados. Cada toque grava autor+timestamp no `escopoLog` (auditoria). As
   * respostas de um setor desmarcado NÃO são apagadas — ficam fora do cálculo e
   * voltam ao re-selecionar (reversível por construção; doc 17b §5.3).
   */
  const alternarSetor = useCallback(
    (setor: string) => {
      if (!auth || SETORES_OBRIGATORIOS.includes(setor)) {
        return;
      }
      editarEscopo((e) => {
        const ativoAgora = e.setoresAtivos.includes(setor);
        const setoresAtivos = ativoAgora
          ? e.setoresAtivos.filter((c) => c !== setor)
          : comObrigatorios([...e.setoresAtivos, setor]);
        const log = [
          ...e.escopoLog,
          { setor, ativo: !ativoAgora, por: auth.nome, em: new Date().toISOString() },
        ];
        return { ...e, setoresAtivos, escopoLog: log };
      });
    },
    [auth, editarEscopo],
  );

  /**
   * Confirma o escopo (libera o checklist). Grava autor + timestamp, salva local
   * (offline-first) e ENFILEIRA na outbox para sincronizar (op própria, sem
   * respostas — doc 17b §6). O servidor revalida o IMGQ restrito ao escopo. Como
   * `escopo` já reflete edições locais pendentes, este é o ponto de commit tanto
   * da 1ª confirmação quanto de uma reabertura ("Salvar setores").
   */
  const confirmarEscopo = useCallback(() => {
    // Defesa em profundidade: afirma o ciclo ativo carregado (avaliacaoAtual.id).
    const avaliacaoId = exigirCiclo(avaliacaoAtual?.id, "confirmar escopo");
    if (!auth || !avaliacaoId) {
      return;
    }
    const confirmado: EscopoData = {
      ...escopo,
      escopoConfirmado: true,
      escopoConfirmadoPor: auth.nome,
      escopoConfirmadoEm: new Date().toISOString(),
    };
    setEscopoLocal((m) => ({ ...m, [avaliacaoId]: confirmado }));
    outbox.enqueue(avaliacaoId, [], { escopo: confirmado });
    atualizarPendencias();
    void sync();
  }, [auth, avaliacaoAtual, escopo, outbox, sync, atualizarPendencias]);

  /**
   * (De)liga a liberação manual da amostra cega deste ciclo (flag no escopo). Só a
   * Supervisão usa. Persiste igual ao `confirmarEscopo` (local + outbox), então o
   * flag sincroniza para o dispositivo de quem preenche a amostra.
   */
  const definirAmostraLiberada = useCallback(
    (valor: boolean) => {
      const avaliacaoId = avaliacaoAtual?.id;
      if (!auth || !avaliacaoId || !podeSupervisionar) {
        return;
      }
      const atualizado: EscopoData = { ...escopo, amostraLiberada: valor };
      setEscopoLocal((m) => ({ ...m, [avaliacaoId]: atualizado }));
      outbox.enqueue(avaliacaoId, [], { escopo: atualizado });
      atualizarPendencias();
      void sync();
    },
    [auth, avaliacaoAtual, escopo, podeSupervisionar, outbox, sync, atualizarPendencias],
  );

  /**
   * Botão "Liberar amostra" da Supervisão: libera a amostra (flag no escopo) E faz
   * um sorteio NOVO resetando as contestações — amostra e contestação são
   * interligadas. Repetível: depois de liberada, funciona como "Refazer".
   */
  const liberarAmostra = useCallback(() => {
    if (!podeSupervisionar) {
      return;
    }
    definirAmostraLiberada(true);
    editarSupervisao(resetarAmostra);
  }, [podeSupervisionar, definirAmostraLiberada, editarSupervisao]);

  /**
   * Recuperação: re-enfileira TODAS as respostas locais do ciclo atual e força o
   * sync. Serve para o dado que ficou só no dispositivo (ex.: operações antigas
   * descartadas por ciclo fora da janela, antes do balde de retidas). O merge do
   * servidor é idempotente/parcial, então reenviar o que já subiu é inofensivo.
   */
  const reenviarRespostasLocais = useCallback(() => {
    const id = avaliacaoAtual?.id;
    if (!id) {
      return;
    }
    const itens = Object.entries(responsesPorAvaliacao[id] ?? {});
    if (itens.length === 0) {
      return;
    }
    for (const [itemId, r] of itens) {
      outbox.enqueue(id, [{ itemId, ...r }]);
    }
    atualizarPendencias();
    void sync();
  }, [avaliacaoAtual, responsesPorAvaliacao, outbox, sync, atualizarPendencias]);

  const patch = useCallback(
    (itemId: string, p: Partial<RespostaItem>) => {
      // Defesa em profundidade: escreve só no ciclo ATIVO de fato carregado
      // (afirma avaliacaoAtual.id, não só o id persistido) e com edição liberada.
      const id = exigirCiclo(avaliacaoAtual?.id, "responder item do checklist");
      if (!id || !editavel) {
        return; // somente leitura: sem ciclo ativo/aberto na janela
      }
      setResponses((r) => ({
        ...r,
        [id]: { ...(r[id] ?? {}), [itemId]: { ...(r[id]?.[itemId] ?? {}), ...p } },
      }));
      outbox.enqueue(id, [{ itemId, ...p }]);
      atualizarPendencias();
      void sync();
    },
    [avaliacaoAtual, editavel, outbox, sync, atualizarPendencias],
  );

  /**
   * Sobe AGORA a captura recém-enfileirada e traduz o desfecho em mensagem para
   * a pessoa. SÓ ela: esperar a fila inteira deixaria o botão em "Enviando…" por
   * minutos em quem tem acúmulo — o resto sobe no drenar periódico.
   */
  const subirCapturaAgora = useCallback(
    async (localId: string, avaliacaoId: string, tok: string): Promise<string | null> => {
      const resultado = await sobTrava(() => drenarEvidencia(fila, api, tok, localId));
      await carregarPendentes();
      await recarregarEvidencias(tok, avaliacaoId);
      if (!resultado) {
        return "Evidência na fila — subindo em segundo plano.";
      }
      // Recusa do servidor: diz o motivo real, em vez de culpar a conexão.
      return resultado.recusadas.find((r) => r.localId === localId)?.motivo ?? null;
    },
    [api, fila, sobTrava, carregarPendentes, recarregarEvidencias],
  );

  const enviarEvidencia = useCallback(
    async (itemId: string, mom: Momento, arquivo: File): Promise<string | null> => {
      // Defesa em profundidade: afirma o ciclo ativo carregado (avaliacaoAtual.id).
      const avaliacaoId = avaliacaoAtual?.id;
      if (!avaliacaoId) {
        return "Selecione uma avaliação.";
      }
      if (!editavel) {
        return "Somente leitura: o ciclo desta unidade não está aberto.";
      }
      // Comprime e gera a miniatura ANTES de enfileirar: a fila offline
      // (IndexedDB) já guarda o arquivo leve, é o arquivo leve que sobe, e a
      // galeria passa a carregar a miniatura em vez da foto inteira. Devolve o
      // original (sem miniatura) se não der para processar.
      const preparada = await prepararEvidencia(arquivo);
      const contentType = tipoDeEvidencia(preparada.arquivo.name, preparada.arquivo.type);
      // Barra AQUI o que o servidor recusaria (formato ou tamanho): enfileirar
      // um arquivo impossível o deixaria parado na frente da fila, travando as
      // próximas evidências — e a pessoa só veria "sem conexão".
      const recusa = motivoRecusaEvidencia(contentType, preparada.arquivo.size);
      if (recusa) {
        return recusa;
      }
      const localId = crypto.randomUUID();
      await fila.enqueue({
        localId,
        avaliacaoId,
        itemId,
        momento: mom,
        contentType,
        blob: preparada.arquivo,
        blobMiniatura: preparada.miniatura ?? undefined,
        criadoEm: Date.now(),
      });
      await carregarPendentes();
      if (token && (typeof navigator === "undefined" || navigator.onLine)) {
        try {
          return await subirCapturaAgora(localId, avaliacaoId, token);
        } catch {
          return "Sem conexão agora — evidência salva na fila.";
        }
      }
      return "Offline — evidência na fila, será enviada ao reconectar.";
    },
    [token, avaliacaoAtual, editavel, fila, subirCapturaAgora, carregarPendentes],
  );

  /** Cancela uma captura ainda NA FILA (não enviada) — puro local, funciona offline. */
  const removerEvidenciaPendente = useCallback(
    async (localId: string): Promise<void> => {
      await fila.remover(localId);
      await carregarPendentes();
    },
    [fila, carregarPendentes],
  );

  /** Apaga uma foto JÁ enviada: enfileira a exclusão e drena (sobe ao reconectar). */
  const excluirEvidencia = useCallback(
    async (ev: EvidenciaRemota): Promise<string | null> => {
      const avaliacaoId = avaliacaoAtual?.id;
      if (!avaliacaoId) {
        return "Selecione uma avaliação.";
      }
      if (!editavel) {
        return "Somente leitura: o ciclo desta unidade não está aberto.";
      }
      await fila.enfileirarExclusao({
        localId: crypto.randomUUID(),
        evidenciaId: ev.id,
        avaliacaoId,
        criadoEm: Date.now(),
      });
      // Some da galeria já (otimista): o reload confirma; se a exclusão falhar no
      // servidor, o re-pull periódico a traz de volta (a operação é idempotente).
      setEvidencias((prev) => {
        const arr = prev[ev.itemId];
        if (!arr) {
          return prev;
        }
        return { ...prev, [ev.itemId]: arr.filter((e) => e.id !== ev.id) };
      });
      if (token && (typeof navigator === "undefined" || navigator.onLine)) {
        try {
          // Só as exclusões, pelo mesmo motivo do anexo: quem espera é a pessoa
          // que apertou o ✕, não a fila de upload inteira.
          await sobTrava(() => drenarExclusoes(fila, api, token));
          await carregarPendentes();
          await recarregarEvidencias(token, avaliacaoId);
          return null;
        } catch {
          return "Sem conexão agora — a foto será removida ao reconectar.";
        }
      }
      return "Offline — remoção na fila, será aplicada ao reconectar.";
    },
    [api, token, avaliacaoAtual, editavel, fila, sobTrava, carregarPendentes, recarregarEvidencias],
  );

  /** Troca a foto: envia a nova e SÓ então remove a antiga (mesma item/momento). */
  const substituirEvidencia = useCallback(
    async (ev: EvidenciaRemota, arquivo: File): Promise<string | null> => {
      const msgEnvio = await enviarEvidencia(ev.itemId, ev.momento as Momento, arquivo);
      const msgExcl = await excluirEvidencia(ev);
      return msgEnvio ?? msgExcl;
    },
    [enviarEvidencia, excluirEvidencia],
  );

  const usuariosApi = useMemo<UsuariosApi>(() => {
    const semSessao = () => Promise.reject(new Error("Entre online como gestor."));
    return {
      listar: () => (token ? api.listarUsuarios(token) : semSessao()),
      criar: (dto) => (token ? api.criarUsuario(dto, token) : semSessao()),
      atualizar: (id, dto) => (token ? api.atualizarUsuario(id, dto, token) : semSessao()),
      redefinirSenha: (id, senha) => (token ? api.redefinirSenha(id, senha, token) : semSessao()),
      senhaProvisoria: (id) => (token ? api.senhaProvisoria(id, token) : semSessao()),
    };
  }, [api, token]);

  const ciclosApi = useMemo<CiclosApi>(() => {
    const semSessao = () => Promise.reject(new Error("Entre online."));
    return {
      listar: () => (token ? api.listarCiclos(token) : semSessao()),
      abrir: (dto) => (token ? api.abrirCiclo(dto, token) : semSessao()),
      encerrar: (id) => (token ? api.encerrarCiclo(id, token) : semSessao()),
      editar: (id, dto) => (token ? api.editarCiclo(id, dto, token) : semSessao()),
      salvarEscopo: (avaliacaoId, escopo) =>
        token ? api.salvarEscopoCiclo(avaliacaoId, escopo, token) : semSessao(),
      resultado: (id) => (token ? api.resultadoImgr(id, token) : semSessao()),
    };
  }, [api, token]);

  const matrizApi = useMemo<MatrizApi>(() => {
    const semSessao = () => Promise.reject(new Error("Entre online como gestor."));
    return {
      obter: (unidadeId) => (token ? api.obterMatriz(unidadeId, token) : semSessao()),
      salvar: (unidadeId, dto) => (token ? api.salvarMatriz(unidadeId, dto, token) : semSessao()),
      listar: () => (token ? api.listarMatrizes(token) : semSessao()),
    };
  }, [api, token]);

  const migracaoApi = useMemo<MigracaoApi>(() => {
    const semSessao = <T>() => Promise.reject<T>(new Error("Entre online."));
    return {
      ingerir: (proposta) => (token ? api.migracaoIngerir(proposta, token) : semSessao()),
      listar: () => (token ? api.migracaoListar(token) : semSessao()),
      lote: (id) => (token ? api.migracaoLote(id, token) : semSessao()),
      impacto: (itemId) => (token ? api.migracaoImpacto(itemId, token) : semSessao()),
      decidir: (loteId, itemId, decisao, codigoDestino) =>
        token ? api.migracaoDecidir(loteId, itemId, decisao, codigoDestino, token) : semSessao(),
      aprovar: (id) => (token ? api.migracaoAprovar(id, token) : semSessao()),
      preflight: (id) => (token ? api.migracaoPreflight(id, token) : semSessao()),
      executar: (id) => (token ? api.migracaoExecutar(id, token) : semSessao()),
      reverter: (id) => (token ? api.migracaoReverter(id, token) : semSessao()),
      manifesto: (id) => (token ? api.migracaoManifesto(id, token) : semSessao()),
    };
  }, [api, token]);

  // Auto-serviço: qualquer usuário troca a própria senha (precisa estar online).
  const trocarSenha = useCallback(
    (senhaAtual: string, novaSenha: string): Promise<void> =>
      token
        ? api.trocarSenha(senhaAtual, novaSenha, token)
        : Promise.reject(new Error("É preciso estar online para trocar a senha.")),
    [api, token],
  );

  return {
    auth,
    unidadeSession,
    token,
    sessaoExpirada,
    online,
    pendingSync,
    syncStatus,
    tab,
    stack,
    momento,
    items,
    itensEscopo,
    itensEscopoSup,
    escopo,
    amostraAlvo,
    checklistCompleto,
    alternarSetor,
    confirmarEscopo,
    imgq,
    qaci,
    ims,
    avaliacoes,
    avaliacaoAtual,
    evolucao,
    panoramaRede,
    podeVerPanorama,
    supervisao,
    podeSupervisionar,
    podeEditarSupervisao: podeSupervisionar && !!avaliacaoAtual,
    editarSupervisao,
    enviarSupervisao,
    liberarAmostra,
    podeLiberarAmostra: podeSupervisionar && !checklistCompletoEstrito,
    editavel,
    reenviarRespostasLocais,
    temRespostasLocais: Object.keys(responsesPorAvaliacao[avaliacaoId ?? ""] ?? {}).length > 0,
    selecionar,
    recarregar,
    ciclosApi,
    matrizApi,
    migracaoApi,
    signIn,
    trocarSenha,
    logout,
    sync,
    switchTab,
    go,
    back,
    setMomento,
    patch,
    evidencias,
    evidenciasPendentes,
    enviarEvidencia,
    excluirEvidencia,
    substituirEvidencia,
    removerEvidenciaPendente,
    usuariosApi,
  };
}
